import { useLocation, useNavigate } from "react-router-dom";
import React, { useState, useRef, useEffect } from "react";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import Header from "@/components/header";
import { AG_GRID_LOCALE_HK } from "@ag-grid-community/locale";
import CustomStatusFilterComponent from "./components/pdfStatusFilter";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community"; // 从 ag-grid-community 导入
import { colorSchemeDarkWarm } from "ag-grid-community";
import CryptoJS from "crypto-js";
import { toast } from "sonner";
import { AuthService } from "@/services/auth";
import { config } from "@/config/env";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import pdfImage from "@/assets/search/pdf.png";
import { useTheme } from "@/contexts/ThemeContext";
import { useTranslation } from "@/locales";
// 深色主题风格
const themeDarkWarm = themeQuartz.withPart(colorSchemeDarkWarm).withParams({
  backgroundColor: "#1d1d1d",
});

// 定义文件数据类型
interface FileData {
  name: string;
  size: number;
  status: string;
  uploader: string;
  md5: string;
  file?: File; // 保存 File 对象
  progress?: number; // 添加进度属性
}
localStorage.removeItem("isUploading");
export default function uploadPdf() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // 判断当前主题是否是深色模式
  // const [isDarkMode, setIsDarkMode] = useState(false);
  const { resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";

  const location = useLocation();
  const knowledgeId = location.state?.knowledgeId || "null"; // 获取传入的知识库ID
  const initialFiles = location.state?.files || []; // 获取传入的文件

  const [files, setFiles] = useState<FileData[]>([]);
  const [isCheckingMd5, setIsCheckingMd5] = useState(false); // 控制MD5查重状态
  const deleteDialogRef = useRef<any>(null);
  const [DialogData, setDialogData] = useState({
    title: "",
    description: "",
    confirmText: "",
  });

  // 在组件挂载后设置翻译
  useEffect(() => {
    setDialogData({
      title: t("common.confirmDelete"),
      description: t("admin.thisActionCannotBeUndone"),
      confirmText: t("common.confirm"),
    });
  }, []);

  // 定义 fileInputRef
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 添加用于取消请求的 AbortController
  const abortControllerRef = useRef<AbortController | null>(null);

  // 待上传文件列表配置
  const columnDefs = [
    {
      headerName: t("admin.fileName"),
      field: "name",
      maxWidth: 800,
      minWidth: 300,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            {/* 固定宽度的图标容器 */}
            <div
              style={{
                width: "24px",
                minWidth: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "8px",
              }}
            >
              <img src={pdfImage} style={{ width: "20px", height: "auto" }} alt="PDF" />
            </div>

            {/* 可省略的文本内容 */}
            <div
              style={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <span>{params.value}</span>
            </div>
          </div>
        );
      },
      filterValueGetter: (params) => {
        return params.data.name;
      },
    },
    { headerName: t("admin.size"), field: "size", valueFormatter: (params) => `${(params.value / (1024 * 1024)).toFixed(2)}M` },
    {
      headerName: t("admin.uploadStatus"),
      field: "status",
      filter: CustomStatusFilterComponent, // 使用自定义过滤器
      cellRenderer: (params: ICellRendererParams) => {
        let style = {};
        let content = params.value;

        if (params.value === "待上传") {
          style = { color: "#F08800" };
          content = t("admin.pendingUpload");
        } else if (params.value === "重复文件") {
          style = { color: "#F92923" };
          content = t("admin.duplicateFile");
        } else if (params.value === "上传成功") {
          style = { color: "#00b83d" };
          content = t("admin.uploadSuccess");
        } else if (params.value === "上传失败") {
          style = { color: "#F92923" };
          content = t("admin.uploadFailed");
        } else if (params.value === "上传中") {
          style = { color: "#1890ff" };
          content = t("status.uploading");
          if (params.data.progress !== undefined) {
            content = `${t("status.uploading")} ${params.data.progress}%`;
          }
        } else if (params.value === "处理中") {
          style = { color: "#1890ff" };
          content = t("admin.processing");
        } else if (params.value === "超出大小") {
          style = { color: "#F92923" };
          content = t("admin.exceedsSize");
        }

        return <span style={style}>{content}</span>;
      },
    },
    // { headerName: t('admin.uploader'), field: 'uploader' },
    {
      headerName: t("admin.operation"),
      filter: false,
      flex: 1,
      maxWidth: 300,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div>
            <span onClick={() => handleDelete(params.data)} style={{ color: "#F92923", cursor: "pointer" }}>
              {t("common.delete")}
            </span>
          </div>
        );
      },
    },
  ];

  const defaultColDef = {
    flex: 1,
    maxWidth: 300,
    minWidth: 300,
    sortable: false,
    cellStyle: {
      userSelect: "text",
      WebkitUserSelect: "text",
      MozUserSelect: "text",
    },
  };

  // 处理从上一步传入的文件
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      // 处理所有文件，包括超过限制的文件
      const oversizedFiles: File[] = [];
      const validFiles: File[] = [];

      initialFiles.forEach((file: File) => {
        if (file.size > 10 * 1024 * 1024) {
          oversizedFiles.push(file);
        } else {
          validFiles.push(file);
        }
      });

      // 处理有效的文件
      if (validFiles.length > 0) {
        processInitialFiles(validFiles);
      }

      // 将超出大小的文件也添加到列表中，状态设为"超出大小"
      if (oversizedFiles.length > 0) {
        const oversizedFileData: FileData[] = oversizedFiles.map((file) => ({
          name: file.name,
          size: file.size,
          status: t("admin.exceedsSize"),
          uploader: t("admin.currentUser"),
          md5: "",
          file: file,
        }));

        setFiles((prevFiles) => [...prevFiles, ...oversizedFileData]);

        // 提示用户
        //const oversizedFileNames = oversizedFiles.map((file: File) => file.name).join('，');
        //toast.error(`以下文件超过了10MB，已添加到列表中（不会上传）：${oversizedFileNames}`);
      }
    }
  }, [initialFiles]);

  // 处理初始文件的MD5去重
  const processInitialFiles = async (validFiles: File[]) => {
    try {
      // 计算所有新文件的MD5值
      const newFilesWithMD5 = await Promise.all(
        validFiles.map(async (file) => {
          const md5 = await calculateFileMD5(file);
          return {
            file,
            md5,
          };
        })
      );

      // 过滤掉本次选择中MD5相同的文件（只保留第一个）
      const md5Map = new Map<string, { file: File; md5: string }>();
      const duplicateInNewFiles: { file: File; md5: string }[] = []; // 收集新文件中的重复项

      newFilesWithMD5.forEach((item) => {
        if (!md5Map.has(item.md5)) {
          md5Map.set(item.md5, item);
        } else {
          // 收集本次选择中重复的文件（用于提示）
          duplicateInNewFiles.push(item);
        }
      });

      const filteredNewFilesWithMD5 = Array.from(md5Map.values());

      // 提示用户本次选择中重复的内容
      if (duplicateInNewFiles.length > 0) {
        //const duplicateFileNames = duplicateInNewFiles.map(({ file }) => file.name).join('，');
        // toast.info(`以下文件内容重复，已自动过滤：${duplicateFileNames}`);
      }

      // 转换为FileData格式
      const newFiles = filteredNewFilesWithMD5.map(({ file, md5 }) => ({
        name: file.name,
        size: file.size,
        status: t("admin.pendingUpload"),
        uploader: t("admin.currentUser"),
        md5: md5,
        file: file,
      }));

      if (newFiles.length > 0) {
        setFiles(newFiles);
        // 提取文件对象用于MD5查重
        const fileObjects = newFiles.map((f) => f.file!);
        // 只对本地去重后的文件进行MD5查重
        checkMd5ForFiles(newFiles, fileObjects);
      }
    } catch (error) {
      console.error("处理初始文件失败:", error);
      toast.error(t("admin.processFileFailed"));
    }
  };

  // 检查并添加文件（避免重复内容的文件）
  const checkAndAddFiles = async (validFiles: File[]) => {
    try {
      // 计算所有新文件的MD5值
      const newFilesWithMD5 = await Promise.all(
        validFiles.map(async (file) => {
          const md5 = await calculateFileMD5(file);
          return {
            file,
            md5,
          };
        })
      );

      // 过滤掉与现有文件MD5相同的文件
      const existingMD5s = new Set(files.map((f) => f.md5).filter((md5) => md5 !== ""));
      const uniqueNewFilesWithMD5 = newFilesWithMD5.filter(({ md5 }) => !existingMD5s.has(md5));

      // 收集与现有文件MD5相同的文件（用于提示）
      const duplicateWithExisting = newFilesWithMD5.filter(({ md5 }) => existingMD5s.has(md5));

      // 过滤掉本次选择中MD5相同的文件（只保留第一个）
      const md5Map = new Map<string, { file: File; md5: string }>();
      const duplicateInNewFiles: { file: File; md5: string }[] = []; // 收集新文件中的重复项

      uniqueNewFilesWithMD5.forEach((item) => {
        if (!md5Map.has(item.md5)) {
          md5Map.set(item.md5, item);
        } else {
          // 收集本次选择中重复的文件（用于提示）
          duplicateInNewFiles.push(item);
        }
      });

      const filteredNewFilesWithMD5 = Array.from(md5Map.values());

      // 提示用户与现有文件重复的内容
      if (duplicateWithExisting.length > 0) {
        //const duplicateFileNames = duplicateWithExisting.map(({ file }) => file.name).join('，');
        //toast.info(`以下文件内容已存在，已自动过滤：${duplicateFileNames}`);
        toast.info(t("admin.duplicateFilesFiltered"));
      }

      // 提示用户本次选择中重复的内容
      if (duplicateInNewFiles.length > 0) {
        //const duplicateFileNames = duplicateInNewFiles.map(({ file }) => file.name).join('，');
        //toast.info(`以下文件内容重复，已自动过滤：${duplicateFileNames}`);
        toast.info(t("admin.duplicateFilesFiltered"));
      }

      // 转换为FileData格式
      const newFiles = filteredNewFilesWithMD5.map(({ file, md5 }) => ({
        name: file.name,
        size: file.size,
        status: t("admin.pendingUpload"),
        uploader: t("admin.currentUser"),
        md5: md5,
        file: file,
      }));

      // 过滤掉已经存在的文件（按文件名）
      const existingFileNames = new Set(files.map((f) => f.name));
      const uniqueNewFiles = newFiles.filter((file) => !existingFileNames.has(file.name));

      // 检查是否超过100条
      if (files.length + uniqueNewFiles.length > 100) {
        alert(t("admin.maxFilesExceeded"));
        // 清空input的value，以便下次选择
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      if (uniqueNewFiles.length > 0) {
        setFiles((prevFiles) => {
          const newFileNames = new Set(uniqueNewFiles.map((f) => f.name));
          const filteredPrevFiles = prevFiles.filter((f) => !newFileNames.has(f.name));
          return [...filteredPrevFiles, ...uniqueNewFiles];
        });

        // 提取文件对象用于MD5查重
        const fileObjects = uniqueNewFiles.map((f) => f.file!);
        checkMd5ForFiles(uniqueNewFiles, fileObjects);
      } else {
        // 如果没有新文件需要添加，也要清空input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } catch (error) {
      console.error("计算文件MD5失败:", error);
      toast.error(t("admin.fileProcessingFailed"));
      // 清空input的value，以便下次选择
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // 处理文件选择
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles) {
      // 分离有效文件和超出大小的文件
      const oversizedFiles: File[] = [];
      const validFiles: File[] = [];

      Array.from(selectedFiles).forEach((file) => {
        if (file.size > 10 * 1024 * 1024) {
          oversizedFiles.push(file);
        } else {
          validFiles.push(file);
        }
      });

      // 处理有效的文件
      if (validFiles.length > 0) {
        checkAndAddFiles(validFiles);
      }

      // 将超出大小的文件也添加到列表中，状态设为"超出大小"
      if (oversizedFiles.length > 0) {
        const oversizedFileData: FileData[] = oversizedFiles.map((file) => ({
          name: file.name,
          size: file.size,
          status: "超出大小",
          uploader: "当前用户",
          md5: "",
          file: file,
        }));

        setFiles((prevFiles) => [...prevFiles, ...oversizedFileData]);

        // 提示用户
        //const oversizedFileNames = oversizedFiles.map((file: File) => file.name).join('，');
        // toast.error(`以下文件超过了10MB，已添加到列表中（不会上传）：${oversizedFileNames}`);
      }

      // 清空input的value，以便下次选择
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // 触发文件选择框
  const openFileChooser = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 计算文件MD5
  const calculateFileMD5 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = function () {
        try {
          const wordArray = CryptoJS.lib.WordArray.create(reader.result as ArrayBuffer);
          const md5 = CryptoJS.MD5(wordArray).toString();
          resolve(md5);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  // MD5查重
  const checkMd5ForFiles = async (newFiles: FileData[], selectedFileObjects: File[]) => {
    setIsCheckingMd5(true); // 开始查重

    try {
      // 并行计算所有文件的MD5
      const md5Promises = selectedFileObjects.map((file) => (file ? calculateFileMD5(file) : Promise.resolve("")));

      const md5Results = await Promise.all(md5Promises);

      // 顺序处理每个文件的查重请求
      for (let i = 0; i < newFiles.length; i++) {
        const fileData = newFiles[i];
        const md5 = md5Results[i];

        // 跳过空文件
        if (!md5) continue;

        try {
          // 调用后端MD5查重接口，使用传入的知识库ID
          const response = await api.get("/v1/admin/knowledge/file/md5", {
            params: {
              knowledgeId: knowledgeId,
              md5: md5,
            },
          });

          console.log("响应体:", response); // 打印响应体，检查是否为

          // 根据实际响应格式判断文件是否存在
          if (response && response.fileId > 0) {
            // 如果存在，更新状态为"重复文件"
            setFiles((prevFiles) => prevFiles.map((f) => (f.name === fileData.name ? { ...f, status: "重复文件" } : f)));
          } else {
            // 如果不存在，更新md5值
            setFiles((prevFiles) => prevFiles.map((f) => (f.name === fileData.name ? { ...f, md5: md5 } : f)));
          }
        } catch (error) {
          console.error("MD5查重失败:", error);

          // 详细的错误处理
          if (error.response) {
            console.error("MD5查重响应错误:", error.response.data);
          } else if (error.request) {
            console.error("MD5查重请求错误:", error.request);
          } else {
            console.error("MD5查重配置错误:", error.message);
          }
        }
      }
    } finally {
      // 查重完成
      setIsCheckingMd5(false);

      // 清空input的value，以便下次选择
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // 删除文件
  const handleDelete = (fileToDelete: FileData) => {
    setFiles(files.filter((file) => file !== fileToDelete));
  };

  // 确认上传
  const handleConfirmUpload = async () => {
    if (files.length === 0) {
      toast.error("没有文件需要上传");
      return;
    }

    // 过滤掉重复文件状态、超出大小的文件和已经上传成功的文件
    const validFiles = files.filter((file) => file.status !== "重复文件" && file.status !== "上传成功" && file.status !== "超出大小" && file.file);

    if (validFiles.length === 0) {
      toast.error("没有有效的文件需要上传");
      return;
    }

    try {
      // 开始上传
      localStorage.setItem("isUploading", "true");
      setIsCheckingMd5(true); // 复用这个状态来表示上传中

      // 循环处理每个文件，按顺序上传
      for (let index = 0; index < validFiles.length; index++) {
        const fileData = validFiles[index];
        const file = fileData.file!; // 获取 File 对象

        const formData = new FormData();
        formData.append("file", file);
        formData.append("knowledgeId", knowledgeId); // 使用传入的知识库ID而不是固定值'1'

        try {
          // 更新状态为上传中
          const fileName = file.name;
          setFiles((prevFiles) => prevFiles.map((f) => (f.name === fileName ? { ...f, status: "上传中", progress: 0 } : f)));

          // 创建 XMLHttpRequest 以跟踪进度
          const xhr = new XMLHttpRequest();

          // 监听上传进度
          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              const percentComplete = Math.round((event.loaded / event.total) * 99);
              setFiles((prevFiles) => prevFiles.map((f) => (f.name === fileName ? { ...f, progress: percentComplete } : f)));
            }
          });

          // 将 XMLHttpRequest 转换为 Promise
          const uploadPromise = new Promise((resolve, reject) => {
            xhr.open("POST", `${config.apiBaseUrl}/v1/admin/knowledge/file/pdf/upload`, true);
            xhr.setRequestHeader("Authorization", `${AuthService.getAccessToken()}`);

            xhr.onload = function () {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve(JSON.parse(xhr.responseText));
              } else {
                reject(new Error(`上传失败: ${xhr.statusText}`));
              }
            };

            xhr.onerror = function () {
              reject(new Error("网络错误"));
            };

            xhr.send(formData);
          });

          // 等待上传完成
          const data: any = await uploadPromise;

          // 检查上传是否成功
          if (data.err) {
            throw new Error(`上传失败: ${data.msg || "未知错误"}`);
          }

          // 进度到100%时，切换状态为处理中
          setFiles((prevFiles) => prevFiles.map((f) => (f.name === fileName ? { ...f, progress: 100, status: "处理中" } : f)));

          // 模拟处理时间（实际应该由服务端推送状态）
          // 这里可以根据实际需要调整或移除
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // 更新上传状态为成功
          setFiles((prevFiles) => prevFiles.map((f) => (f.name === fileName ? { ...f, status: "上传成功" } : f)));

          // 显示上传进度提示
          toast.success(t("admin.fileUploadSuccess", { fileName, current: index + 1, total: validFiles.length }));
        } catch (err: any) {
          // 更新上传状态为失败
          const fileName = file.name;
          setFiles((prevFiles) => prevFiles.map((f) => (f.name === fileName ? { ...f, status: "上传失败" } : f)));

          console.error("上传失败:", err);
          toast.error(t("admin.fileUploadError", { fileName, error: err.message || t("common.networkError") }));
        }
      }

      // 上传完成后清空未成功的文件列表（除了失败的文件）
      setFiles((prevFiles) => prevFiles.filter((file) => file.status === "上传成功" || file.status === "上传失败" || file.status === "重复文件"));

      // 清空文件输入框
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // 检查是否有失败的文件
      const failedFiles = files.filter((file) => file.status === "上传失败");
      if (failedFiles.length > 0) {
        // toast.error("部分文件上传失败，请查看列表");
      } else {
        // toast.success("文件上传完成");
      }

      // 只有当没有待上传文件时才返回上一页
      const pendingFiles = files.filter((file) => file.status === "待上传" || file.status === "上传中");
      if (pendingFiles.length === 0) {
        // navigate(-1);
      }
    } catch (err: any) {
      console.error("上传过程出错:", err);
      toast.error(err.message || t("admin.uploadProcessError"));
    } finally {
      // 无论成功失败都结束上传状态
      setIsCheckingMd5(false);
      localStorage.removeItem("isUploading");
    }
  };

  // 组件挂载时添加事件监听器
  useEffect(() => {
    const handleAbortUpload = () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };

    window.addEventListener("abortUpload", handleAbortUpload);

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      localStorage.removeItem("isUploading");
      window.removeEventListener("abortUpload", handleAbortUpload);
    };
  }, []);

  // 监听路由变化
  useEffect(() => {
    // 当路由变化时，如果正在上传则取消请求
    if (isCheckingMd5 && abortControllerRef.current) {
      abortControllerRef.current.abort();
      localStorage.setItem("isUploading", "false");
      setIsCheckingMd5(false);
      abortControllerRef.current = null;
    }
  }, [location.key, isCheckingMd5]);

  // 返回按钮
  const goBack = () => {
    if (localStorage.getItem("isUploading") === "true") {
      toast.info(t("admin.fileUploadingPleaseWait"));
      return;
    }
    navigate(-1);
  };
  return (
    <main className="pl-4 pr-4 py-12 pt-0 dark:bg-[#1d1d1d] bg-[#ffffff]" style={{ width: "100%" }}>
      <Header title={t("admin.fileUpload")} />
      <div className="space-y-4">
        <div className="flex justify-between items-center mx-auto space-y-4 mt-5">
          <h2 className="text-xl font-bold mb-0" style={{ cursor: "pointer" }} onClick={() => navigate(-1)}>
            <span className="whitespace-nowrap" style={{ fontSize: "16px" }}>
              <span style={{ color: "#9c9c9c" }}>{t("admin.fileSet")}/</span>
              {t("admin.uploadFile")}
            </span>
          </h2>
          <div className="space-x-2">
            <Button onClick={openFileChooser} disabled={isCheckingMd5}>
              + {t("admin.scanFile")}
            </Button>
            <input type="file" multiple accept=".pdf" ref={fileInputRef} onChange={handleFileSelect} style={{ display: "none" }} />
            <Button variant="outline" onClick={goBack}>
              {t("common.back")}
            </Button>
          </div>
        </div>

        <div className="ag-theme-alpine" style={{ height: "70vh", width: "100%" }}>
          <AgGridReact
            theme={isDarkMode ? themeDarkWarm : themeQuartz} //themeQuartz 默认主题风格
            columnDefs={columnDefs}
            rowData={files}
            defaultColDef={defaultColDef}
            rowHeight={50}
            localeText={AG_GRID_LOCALE_HK}
          />
        </div>
        <div className="space-x-2">
          <Button onClick={handleConfirmUpload} disabled={isCheckingMd5}>
            {t("admin.confirmUpload")}
          </Button>
        </div>
      </div>

      {/* 确认弹窗 */}
      <ConfirmDialog ref={deleteDialogRef} title={DialogData.title} description={DialogData.description} confirmText={DialogData.confirmText} onConfirm={() => {}} />
    </main>
  );
}
